import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { Issuer } from '@arrived/models';

import { ROUTES } from './Routes';

interface StandardDisclaimerContentProps {
  issuerName?: Issuer['name'];
  LinkElement: SharedLinkElement;
  lineBreakElement: ReactElement;
}

type SharedLinkElement = ({ href, text }: { href: string; text: string | ReactElement }) => ReactElement;

const DISCLAIMER_TEXT = `Arrived Holdings, Inc. ("Arrived"), as a manager of {LLCName} operates the \
{arrivedHomepageLink} website (the "Site") and is not a broker-dealer or investment advisor. All securities related \
activity is conducted through Dalmore Group LLC, a registered broker-dealer and member of {FINRALink}/{SIPCLink}, \
located at 525 Green Place, Woodmere, NY 11598. You can review the {dalmoreBrokerCheckLink}. An up-to-date \
{dalmoreFormCRSLink} is available. \
{br}{br} \
You should speak with your financial advisor, accountant and/or attorney when evaluating any offering. Neither \
Arrived, {LLCName}, nor Dalmore makes any recommendations or provides advice about investments, and no communication, \
through this website or in any other medium, should be construed as a recommendation for any security offered on \
or off this investment platform. The Site may make forward-looking statements. You should not rely on these \
statements but should carefully evaluate the offering materials in assessing any investment opportunity, including \
the complete set of risk factors that are provided as part of the offering circular for your consideration. \
{br}{br} \
{LLCName} is conducting public offerings pursuant to Regulation A, as amended, through the Site. The \
{offeringCircularLink} is available. Past performance is no guarantee of future results. Investments such as \
those on the Arrived platform are speculative and involve substantial risks to consider before investing, \
outlined in the respective offering materials and including, but not limited to, illiquidity, lack of \
diversification and complete loss of capital. Key risks include, but are not limited to, limited operating history, \
limited diversification, risk of asset damage or theft and lack of voting rights. Also, the adverse economic \
effects of the COVID-19 pandemic remain unknown and could materially impact this investment. An investment in an \
offering constitutes only an investment in a particular series and not in {LLCName} or the underlying asset(s). \
Investors should carefully review the risks located in the respective offering materials for a more comprehensive \
discussion of risk. \
{br}{br} \
From time to time, {LLCName} will seek to qualify additional series offerings of securities under regulation A. \
For offerings that have not yet been qualified, no money or other consideration is being solicited and, if sent in \
response, will not be accepted. No offer to buy securities of a particular offering can be accepted, and no part \
of the purchase price can be received, until an offering statement filed with the Securities and Exchange \
Commission (the "SEC") relating to that series has been qualified by the SEC. Any such offer may be \
withdrawn or revoked, without obligation or commitment of any kind, at any time before notice of acceptance given \
after the date of qualification by the SEC. An indication of interest involves no obligation or commitment of any kind. \
{br}{br} \
Investment overviews contained herein contain summaries of the purpose and the principal business terms of the \
investment opportunities. Such summaries are intended for informational purposes only and do not purport to be \
complete, and each is qualified in its entirety by reference to the more-detailed discussions contained in the \
respective offering circular filed with the SEC. \
{br}{br} \
{LLCName} does not offer refunds after an investment has been made. Please review the relevant offering \
materials and subscription documentation for more information. \
{br}{br} \
An active trading market for any series of {LLCName} interests may not develop or be sustained. If an active \
public trading market for {LLCName} series interests does not develop or is not sustained, it may be difficult \
or impossible for you to resell your interests at any price. Even if an active market does develop, the market \
price could decline below the amount you paid for your interests. There is no assurance that the Arrived \
platform will provide an active market for resales of {LLCName} series interests. Further, without the Arrived \
platform, it may be difficult or impossible for you to dispose of your interests. If the market develops for \
any series of {LLCName} interests, the market price of such interests could fluctuate significantly for many \
reasons, including reasons unrelated to performance, the underlying assets or any series, such as reports by \
industry analysts, investor perceptions or announcements by competitors regarding their own performance, as well \
as general economic and industry conditions. \
{br}{br} \
For additional risk factors and disclaimer information, you can review our {communicationsDisclaimerLink}.`;

export const StandardDisclaimerContent = ({
  issuerName = 'Arrived Homes, LLC',
  LinkElement,
  lineBreakElement,
}: StandardDisclaimerContentProps) => {
  return (
    <FormattedMessage
      id="standard-disclaimer-text"
      defaultMessage={DISCLAIMER_TEXT}
      values={{
        br: lineBreakElement,
        LLCName: issuerName,
        arrivedHomepageLink: <LinkElement href="https://arrived.com/" text="arrived.com" />,
        FINRALink: <LinkElement href="https://www.finra.org/" text="FINRA" />,
        SIPCLink: <LinkElement href="https://www.sipc.org/" text="SIPC" />,
        dalmoreBrokerCheckLink: (
          <LinkElement
            href="https://brokercheck.finra.org/"
            text={
              <FormattedMessage
                id="standard-disclaimer-dalmore-brokercheck-link-text"
                defaultMessage="brokercheck for Dalmore"
              />
            }
          />
        ),
        dalmoreFormCRSLink: (
          <LinkElement
            href="https://app.box.com/s/hlyq8kust4t786703q6ox7ehh5b6nvou"
            text={
              <FormattedMessage id="standard-disclaimer-dalmore-form-crs-link-text" defaultMessage="Dalmore Form CRS" />
            }
          />
        ),
        offeringCircularLink: (
          <LinkElement
            href={`${ROUTES.static.base}${ROUTES.public.offeringCircular}`}
            text={
              <FormattedMessage
                id="standard-disclaimer-offering-circular-link-text"
                defaultMessage={`offering circular for {issuerName}`}
                values={{ issuerName }}
              />
            }
          />
        ),
        communicationsDisclaimerLink: (
          <LinkElement
            href={`${ROUTES.static.base}${ROUTES.communicationsDisclaimer}`}
            text={
              <FormattedMessage
                id="standard-disclaimer-communications-disclaimer-link-text"
                defaultMessage="communications disclaimer"
              />
            }
          />
        ),
      }}
    />
  );
};
